import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import api from '../services/api';
import { LineItemProvisioningState, ProvisioningStepState } from '../types/order';

export type PersistedOrderItem = {
  product_id?: string;
  product_name?: string;
  product_type?: string;
  service_type_code?: string | null;
  cart_line_index?: number;
  instance_index?: number;
  quantity?: number;
  unit_price?: number;
  total_price?: number;
  configuration?: Record<string, unknown> | null;
  plan_features?: Record<string, string> | null;
};

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatConfigValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    const parts = value.map((v) => (v == null ? '' : String(v))).filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const FIELD_ORDER = ['name', 'description', 'monthly_price', 'contract_terms', 'service_address'];

// Keys handled separately in the porting details section — excluded from the main service details grid.
const PORTING_KEYS = new Set([
  'number_type',
  'porting_mobile_number',
  'porting_fixed_numbers',
  'porting_current_provider',
  'porting_current_account_number',
  'porting_document_path',
  'quantity',  // system key — shown via the Qty column, not the service details grid
  'did_count', // wizard-only field, not a stored service detail
]);

function sortConfigKeys(keys: string[]): string[] {
  const priority = keys.filter((k) => FIELD_ORDER.includes(k)).sort((a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b));
  const rest = keys.filter((k) => !FIELD_ORDER.includes(k)).sort((a, b) => a.localeCompare(b));
  return [...priority, ...rest];
}

function sortItems(items: PersistedOrderItem[]): PersistedOrderItem[] {
  return items.slice().sort((a, b) => {
    const la = (a.cart_line_index ?? 0) - (b.cart_line_index ?? 0);
    if (la !== 0) return la;
    return (a.instance_index ?? 0) - (b.instance_index ?? 0);
  });
}

const PORTING_FIELD_LABELS: Record<string, string> = {
  porting_mobile_number: 'Mobile number',
  porting_current_provider: 'Current provider',
  porting_current_account_number: 'Account number',
};

function PortingDetails({ cfg }: { cfg: Record<string, unknown> }) {
  const numberType = String(cfg.number_type ?? 'new_number');
  const isPortIn = numberType === 'port_in';

  if (!isPortIn) {
    return (
      <Box sx={{ mt: 1 }}>
        <Chip
          label="New number — assigned at provisioning"
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20, borderColor: 'info.light', color: 'info.dark' }}
        />
      </Box>
    );
  }

  const portingRows = Object.entries(PORTING_FIELD_LABELS)
    .map(([key, label]) => ({ label, value: String(cfg[key] ?? '—') }));

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 0.75 }}>
        Porting details
      </Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(8rem,40%) 1fr', gap: 0.5, columnGap: 1 }}>
        {portingRows.map(({ label, value }) => (
          <React.Fragment key={label}>
            <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, wordBreak: 'break-word' }}>
              {value || '—'}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

const VOICE_PORTING_FIELD_LABELS: Record<string, string> = {
  porting_fixed_numbers: 'Fixed number(s)',
  porting_current_provider: 'Current provider',
  porting_current_account_number: 'Account number',
};

function VoicePortingDetails({
  cfg,
  orderId,
  cartLineIndex,
  instanceIndex,
  onViewDocument,
}: {
  cfg: Record<string, unknown>;
  orderId?: string;
  cartLineIndex?: number;
  instanceIndex?: number;
  onViewDocument?: (orderId: string, cartLineIndex: number, instanceIndex: number) => void;
}) {
  const numberType = String(cfg.number_type ?? 'new_number');
  const isPortIn = numberType === 'port_in';

  if (!isPortIn) {
    return (
      <Box sx={{ mt: 1 }}>
        <Chip
          label="New number — assigned at provisioning"
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20, borderColor: 'info.light', color: 'info.dark' }}
        />
      </Box>
    );
  }

  const portingRows = Object.entries(VOICE_PORTING_FIELD_LABELS)
    .map(([key, label]) => ({ label, value: String(cfg[key] ?? '—') }));

  const hasDoc = Boolean(cfg.porting_document_path);
  const canViewDoc = hasDoc && orderId && cartLineIndex != null && instanceIndex != null && onViewDocument;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 0.75 }}>
        Fixed number porting
      </Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(8rem,40%) 1fr', gap: 0.5, columnGap: 1 }}>
        {portingRows.map(({ label, value }) => (
          <React.Fragment key={label}>
            <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
              {value || '—'}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
      {canViewDoc && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<InsertDriveFileOutlinedIcon />}
            onClick={() => onViewDocument!(orderId!, cartLineIndex!, instanceIndex!)}
            sx={{ fontSize: '0.75rem', py: 0.25 }}
          >
            View porting document
          </Button>
        </Box>
      )}
      {hasDoc && !canViewDoc && (
        <Box sx={{ mt: 0.75 }}>
          <Chip
            label="Porting document uploaded"
            size="small"
            icon={<InsertDriveFileOutlinedIcon />}
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
      )}
    </Box>
  );
}

function allPrereqsDone(steps: ProvisioningStepState[]): boolean {
  return steps.filter((s) => !s.is_final_step).every((s) => s.is_complete);
}

function ProvisioningStepButtons({
  lineItem,
  onToggle,
  onRadiusStep,
  onAccountServiceStep,
  onSupaProvisionStep,
  onStaticIpStep,
}: {
  lineItem: LineItemProvisioningState;
  onToggle: (cartLineIndex: number, stepKey: string) => Promise<void>;
  onRadiusStep?: (cartLineIndex: number) => void;
  onAccountServiceStep?: (cartLineIndex: number) => void;
  onSupaProvisionStep?: (cartLineIndex: number) => void;
  onStaticIpStep?: (cartLineIndex: number) => void;
}) {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const prereqsDone = allPrereqsDone(lineItem.steps);

  const handleClick = async (step: ProvisioningStepState) => {
    if (loadingKey) return;
    if (step.is_final_step && !prereqsDone && !step.is_complete) return;
    // supa_provision always opens the modal — never direct toggle
    if (step.key === 'supa_provision' && onSupaProvisionStep) {
      onSupaProvisionStep(lineItem.cart_line_index);
      return;
    }
    // radius_credentials always opens the modal (new or edit) — never direct toggle
    if (step.key === 'radius_credentials' && onRadiusStep) {
      onRadiusStep(lineItem.cart_line_index);
      return;
    }
    // assign_ip always opens the form (enter / edit the assigned IP) — never direct toggle
    if (step.key === 'assign_ip' && onStaticIpStep) {
      onStaticIpStep(lineItem.cart_line_index);
      return;
    }
    // account_service always opens the modal (new or edit) — never direct toggle
    if (step.key === 'account_service' && onAccountServiceStep) {
      onAccountServiceStep(lineItem.cart_line_index);
      return;
    }
    setLoadingKey(step.key);
    try {
      await onToggle(lineItem.cart_line_index, step.key);
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'nowrap' }}>
      {lineItem.steps.map((step) => {
        const isLoading = loadingKey === step.key;
        const isLocked = step.is_final_step && !prereqsDone && !step.is_complete;
        const tooltipTitle = step.is_complete
          ? `${step.label}${step.completed_by_name ? ` — done by ${step.completed_by_name}` : ''}`
          : isLocked
          ? `${step.label} — complete all prior steps first`
          : step.label;
        const labelColor = step.is_complete
          ? 'success.main'
          : isLocked
          ? 'text.disabled'
          : 'text.secondary';

        return (
          <Tooltip key={step.key} title={tooltipTitle} placement="top">
            <Box
              component="span"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
                cursor: isLocked || !!loadingKey ? 'default' : 'pointer',
                flexShrink: 0,
              }}
              onClick={() => !isLocked && !loadingKey && handleClick(step)}
            >
              <IconButton
                size="small"
                disabled={isLocked || !!loadingKey}
                sx={{ p: 0.5, pointerEvents: 'none' }}
                tabIndex={-1}
              >
                {isLoading ? (
                  <CircularProgress size={18} />
                ) : step.is_complete ? (
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                ) : isLocked ? (
                  <LockIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                )}
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  display: { xs: 'none', xl: '-webkit-box' },
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.6rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  color: labelColor,
                  maxWidth: 72,
                }}
              >
                {step.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

type AdminOrderLineItemsPanelProps = {
  orderItems: unknown;
  orderId?: string;
  provisioningSteps?: LineItemProvisioningState[];
  onToggleStep?: (cartLineIndex: number, stepKey: string) => Promise<void>;
  onRadiusStep?: (cartLineIndex: number) => void;
  onAccountServiceStep?: (cartLineIndex: number) => void;
  onSupaProvisionStep?: (cartLineIndex: number) => void;
  onStaticIpStep?: (cartLineIndex: number) => void;
};

/**
 * Renders persisted `order_items.items` with per-instance configuration (staff multi-line orders).
 */
function AdminOrderLineItemsPanel({ orderItems, orderId, provisioningSteps, onToggleStep, onRadiusStep, onAccountServiceStep, onSupaProvisionStep, onStaticIpStep }: AdminOrderLineItemsPanelProps) {
  const [docViewer, setDocViewer] = useState<{ url: string; isPdf: boolean } | null>(null);

  const handleViewDocument = useCallback(async (oid: string, cartLineIndex: number, instanceIndex: number) => {
    try {
      const { objectUrl, isPdf } = await api.fetchPortingDocumentBlob(oid, cartLineIndex, instanceIndex);
      setDocViewer({ url: objectUrl, isPdf });
    } catch {
      // surface nothing — caller can see network errors in dev tools
    }
  }, []);

  const handleCloseDoc = useCallback(() => {
    if (docViewer?.url) URL.revokeObjectURL(docViewer.url);
    setDocViewer(null);
  }, [docViewer]);

  const items = useMemo(() => {
    const raw = orderItems as { items?: unknown } | null | undefined;
    const arr = raw?.items;
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is PersistedOrderItem => x != null && typeof x === 'object');
  }, [orderItems]);

  const rows = useMemo(() => sortItems(items), [items]);
  const hasActions = provisioningSteps && provisioningSteps.length > 0;
  const provisioningMap = useMemo(() => {
    const m: Record<number, LineItemProvisioningState> = {};
    (provisioningSteps || []).forEach((li) => { m[li.cart_line_index] = li; });
    return m;
  }, [provisioningSteps]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No line items were stored on this order (legacy or empty cart).
      </Typography>
    );
  }

  return (
    <>
      <Dialog
        open={Boolean(docViewer)}
        onClose={handleCloseDoc}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Porting document
          <IconButton size="small" onClick={handleCloseDoc}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: 480 }}>
          {docViewer && (
            docViewer.isPdf ? (
              <iframe
                src={docViewer.url}
                title="Porting document"
                style={{ width: '100%', height: 560, border: 'none' }}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <img
                  src={docViewer.url}
                  alt="Porting document"
                  style={{ maxWidth: '100%', maxHeight: 540, objectFit: 'contain' }}
                />
              </Box>
            )
          )}
        </DialogContent>
      </Dialog>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell width={56} align="center">Qty</TableCell>
            <TableCell>Product</TableCell>
            <TableCell width={110} align="right">Monthly</TableCell>
            <TableCell>Service details</TableCell>
            {hasActions && <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((it, idx) => {
            const cfg = it.configuration && typeof it.configuration === 'object' ? it.configuration : null;
            const serviceKeys = cfg
              ? sortConfigKeys(Object.keys(cfg).filter((k) => !PORTING_KEYS.has(k)))
              : [];
            const isMobile = (it.service_type_code || '').toLowerCase() === 'mobile';
            const isVoice = (it.service_type_code || '').toLowerCase() === 'voice';
            const isNbn = (it.service_type_code || '').toLowerCase() === 'nbn';
            const hasPortingInfo = cfg != null && 'number_type' in cfg;
            const qty =
              it.quantity != null && Number.isFinite(Number(it.quantity)) ? Number(it.quantity) : 1;
            const monthlyValue = it.total_price != null ? it.total_price : it.unit_price;
            const monthly =
              monthlyValue != null && Number.isFinite(Number(monthlyValue))
                ? `$${Number(monthlyValue).toFixed(2)}`
                : '—';
            return (
              <TableRow key={`${it.product_id ?? 'row'}-${it.cart_line_index ?? 0}-${it.instance_index ?? idx}-${idx}`}>

                <TableCell align="center">{qty}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {it.product_name || 'Product'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {it.service_type_code || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{monthly}</TableCell>
                <TableCell>
                  {isNbn ? (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  ) : serviceKeys.length === 0 && !hasPortingInfo ? (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  ) : (
                    <Box>
                      {serviceKeys.length > 0 && (
                        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(8rem,40%) 1fr', gap: 0.5, columnGap: 1 }}>
                          {serviceKeys.map((k) => (
                            <React.Fragment key={k}>
                              <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {humanizeKey(k)}
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {k === 'monthly_price' ? `$${formatConfigValue(cfg![k])}` : formatConfigValue(cfg![k])}
                              </Typography>
                            </React.Fragment>
                          ))}
                        </Box>
                      )}
                      {isMobile && hasPortingInfo && cfg && (
                        <PortingDetails cfg={cfg} />
                      )}
                      {isVoice && hasPortingInfo && cfg && (
                        <VoicePortingDetails
                          cfg={cfg}
                          orderId={orderId}
                          cartLineIndex={it.cart_line_index}
                          instanceIndex={it.instance_index}
                          onViewDocument={handleViewDocument}
                        />
                      )}
                    </Box>
                  )}
                </TableCell>
                {hasActions && (
                  <TableCell>
                    {(() => {
                      const cli = it.cart_line_index ?? null;
                      const li = cli !== null ? provisioningMap[cli] : undefined;
                      if (!li || li.steps.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
                      return (
                        <ProvisioningStepButtons
                          lineItem={li}
                          onToggle={onToggleStep ?? (() => Promise.resolve())}
                          onRadiusStep={onRadiusStep}
                          onAccountServiceStep={onAccountServiceStep}
                          onSupaProvisionStep={onSupaProvisionStep}
                          onStaticIpStep={onStaticIpStep}
                        />
                      );
                    })()}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
}

export default AdminOrderLineItemsPanel;
